import { useEffect, useMemo, useRef } from 'react';
import { buildBlockQueryArgs, calcSpecPagesGuarded, derivePageFromBlock, makeScopeKeyForFilters } from './rtkq_block_pager';
import { commitSpeculativeObservation } from './speculative-pages';

/**
 * Универсальный хук «блочной пагинации» поверх любого RTK Query createApi.
 * — Считает limit/offset для запроса «нескольких страниц за раз».
 * — Режет текущую страницу из полученного блока.
 * — Считает «спекулятивные страницы», держит гипотетическую следующую, учитывает isLoading.
 * — Централизованно фиксирует максимум страниц и «конец данных» (в памяти процесса).
 *
 * Пример использования см. JSDoc к параметрам (championships/teams и т.п.).
 */
export function useBlockPagerCore<TItem, TArgs extends Record<string, any>, TFilters extends Record<string, any>>(params: {
  // RTK Query-хук эндпоинта списка (например, useListChampionshipsQuery)
  useQueryHook: (
    args: TArgs & { limit: number; offset: number },
    options?: any,
  ) => { data?: TItem[]; isFetching: boolean; isError?: boolean; error?: any };

  // Обязательные параметры пагинации
  page: number;
  perPage: number;

  // Фильтры и маппинг в аргументы эндпоинта
  filters?: TFilters;
  mapFiltersToArgs: (filters: TFilters | undefined) => Partial<TArgs>;
  baseArgs?: Partial<TArgs>;

  // Настройки блочной пагинации
  rawPrefetchPages?: number; // по умолчанию 3
  maxBackendLimit?: number; // кап лимита бэка, например 20

  // Ключ скоупа для накопления максимумов/hasMore (entity + стабильные фильтры)
  scopeEntity: string;
  scopeParts?: Record<string, any>; // что добавить в ключ дополнительно к фильтрам

  // Опции запроса (например, { skip: !id })
  getQueryOptions?: (args: TArgs & { limit: number; offset: number }) => any;
}) {
  const {
    useQueryHook,
    page,
    perPage,
    filters,
    mapFiltersToArgs,
    baseArgs,
    rawPrefetchPages = 3,
    maxBackendLimit = 20,
    scopeEntity,
    scopeParts,
    getQueryOptions,
  } = params;

  // 1) Сбор аргументов запроса блока
  const { queryArgs, blockLimit, blockOffset, pageIndexInsideBlock } = useMemo(() => {
    return buildBlockQueryArgs<TArgs, TFilters>({
      page,
      perPage,
      rawPrefetchPages,
      maxBackendLimit,
      filters,
      baseArgs,
      mapFiltersToArgs,
    });
  }, [page, perPage, rawPrefetchPages, maxBackendLimit, filters, baseArgs, mapFiltersToArgs]);

  // 2) Стабильный ключ запроса для защиты от поздних ответов
  const queryKey = useMemo(() => {
    const stable = { ...(queryArgs as any) };
    return JSON.stringify(stable);
  }, [queryArgs]);
  const lastCommittedKeyRef = useRef<string>('');

  // 3) Вызов RTKQ useQuery для блока
  const options = useMemo(() => (getQueryOptions ? getQueryOptions(queryArgs) : undefined), [getQueryOptions, queryArgs]);
  const { data: block = [], isFetching: isLoading, isError = false, error } = useQueryHook(queryArgs, options);

  // 4) Режем текущую страницу
  const itemsForPage = useMemo(
    () => derivePageFromBlock(block, pageIndexInsideBlock, perPage),
    [block, pageIndexInsideBlock, perPage],
  );

  // 5) Ключ скоупа (entity + фильтры + опциональные части)
  const scopeKey = useMemo(
    () => makeScopeKeyForFilters(scopeEntity, { ...(filters || {}), ...(scopeParts || {}) }),
    [scopeEntity, filters, scopeParts],
  );

  // 6) «Спекулятивные страницы» с учётом загрузки
  const spec = useMemo(
    () =>
      calcSpecPagesGuarded({
        scopeKey,
        page,
        perPage,
        blockOffset,
        blockLength: Array.isArray(block) ? block.length : 0,
        requestedLimit: blockLimit,
        isLoading,
      }),
    [scopeKey, page, perPage, blockOffset, block, blockLimit, isLoading],
  );

  // 7) Фиксация наблюдения (максимум страниц и «конец данных»)
  useEffect(() => {
    if (isLoading) return;
    if (lastCommittedKeyRef.current !== queryKey) {
      commitSpeculativeObservation(scopeKey, { maxKnownPages: spec.maxKnownPages, provenNoMore: spec.provenNoMore });
      lastCommittedKeyRef.current = queryKey;
    }
  }, [isLoading, queryKey, scopeKey, spec.maxKnownPages, spec.provenNoMore]);

  return {
    queryArgs,
    block,
    itemsForPage,
    isLoading,
    isError,
    error,
    pages: spec.pages,
    spec,
    blockLimit,
    blockOffset,
    pageIndexInsideBlock,
    scopeKey,
  } as const;
}

export default useBlockPagerCore;
